import React from "react";

/**
 * Catches render-time errors so one broken component does not blank the whole
 * app. Without this, any uncaught exception during render unmounts the entire
 * React tree and the user sees a white screen with no explanation.
 *
 * Deliberately plain: it must not depend on context, routing, or data that
 * might itself be the thing that failed.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Keep the real error in the console for debugging; it is never shown to
    // the user in production.
    console.error("Render error caught by ErrorBoundary:", error, info);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = "/dashboard";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isDev = process.env.NODE_ENV !== "production";

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
          background: "#f6f8fa",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            background: "#fff",
            border: "1px solid #d0d7de",
            borderRadius: "12px",
            padding: "32px",
            textAlign: "center",
          }}
        >
          <h1 style={{ margin: "0 0 8px", fontSize: "20px", color: "#1f2328" }}>
            Something went wrong
          </h1>
          <p
            style={{
              margin: "0 0 24px",
              fontSize: "14px",
              color: "#57606a",
              lineHeight: 1.6,
            }}
          >
            This page ran into an unexpected error. Reloading usually fixes it.
          </p>

          {isDev && this.state.error && (
            <pre
              style={{
                textAlign: "left",
                fontSize: "12px",
                background: "#f6f8fa",
                border: "1px solid #d0d7de",
                borderRadius: "6px",
                padding: "12px",
                overflowX: "auto",
                marginBottom: "20px",
                color: "#cf222e",
              }}
            >
              {String(this.state.error?.stack || this.state.error)}
            </pre>
          )}

          <div
            style={{ display: "flex", gap: "12px", justifyContent: "center" }}
          >
            <button
              onClick={this.handleReload}
              style={{
                background: "#4253C7",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "10px 20px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Reload page
            </button>
            <button
              onClick={this.handleHome}
              style={{
                background: "#fff",
                color: "#4253C7",
                border: "1px solid #4253C7",
                borderRadius: "8px",
                padding: "10px 20px",
                fontSize: "14px",
                cursor: "pointer",
              }}
            >
              Go to dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
